/**
 * MonetizationPage
 * ================
 *
 * Seller-only surface for everything to do with making money on the
 * data marketplace. Replaces the old "My listings" entry point that
 * lived inside the Marketplace and Data Signals pages.
 *
 * Layout follows ``ModelsPage`` (Pipelines) so the page reads as
 * native platform chrome: ``PageBreadcrumbs`` + title + subtitle +
 * Tabs + ``DataTable`` for the listings + ``ListingToolbar`` for
 * filters. Padding, gutters, breadcrumb spacing are intentionally
 * matched.
 *
 * Tabs:
 *
 *   1. Dashboard           — KPI tiles + at-a-glance numbers.
 *   2. Published Signals   — DataTable of every signal you've
 *                            published, with status, subscribers,
 *                            MRR, lifetime, edit / delist actions.
 *   3. Earnings            — Accrued earnings balance + per-month
 *                            totals + recent charges. Earnings are
 *                            paid out by the platform team on a
 *                            monthly cycle (no self-serve cashout).
 *
 * Selling requires the workspace's organization to be a VERIFIED
 * PROVIDER. The banner at the top of the page surfaces the current
 * verification state and hosts the application flow (org owner/admin
 * only). Personal workspaces cannot sell.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, FormControlLabel, Grid, Stack, Tab, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentsIcon from '@mui/icons-material/Payments';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import VerifiedIcon from '@mui/icons-material/Verified';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import RowActions from '../common/RowActions';
import EntityCell from '../common/EntityCell';
import {
  CMButton, CMButtonOutlined, CMButtonError,
} from '../../containers/styledComponents';

import {
  fetchMyListings, fetchEarnings, fetchSellerStatus,
  applyForProvider, deleteListing, clearMutations,
} from '../../redux/actions/marketplace/listings';

import {
  KIND_LABELS,
  usdFromCredits, formatUSD, useUsdRate,
  pricingModelOf, PRICING_MODEL_LABELS, priceSummary, niceDate,
} from './marketplaceUtils';


// ---------------------------------------------------------------------------
// Tab routing — sync the active tab with the ``?tab=`` URL param so
// users can deep-link, e.g. /monetization?tab=payouts.
// ---------------------------------------------------------------------------

const TAB_KEYS = ['dashboard', 'published', 'payouts'];
const TAB_LABEL = {
  dashboard: 'Overview',
  published: 'Published Signals',
  payouts:   'Payouts',
};

// Legacy deep links: the tab used to be called "earnings" (and before
// that "cashout"); keep those URLs working.
const normalizeTabKey = (k) => (
  k === 'earnings' || k === 'cashout' ? 'payouts' : k
);

// The BE returns the last 12 months that HAD settled charges (sorted
// desc), so monthly[0] is not necessarily the current month — resolve
// "this month" explicitly to avoid showing a stale month's total.
const currentMonthRow = (monthly) => {
  const now = new Date();
  const key = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  return (monthly || []).find((m) => m.month === key) || null;
};


// ---------------------------------------------------------------------------
// Reusable KPI tile (matches the style on Pipelines / AdminOverview).
// ---------------------------------------------------------------------------

const KPITile = ({ label, value, sub, icon, color }) => {
  const theme = useTheme();
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {icon && (
            <Box sx={{
              p: 1, borderRadius: 1.5,
              bgcolor: alpha(color || theme.palette.primary.main, 0.1),
              color:   color || theme.palette.primary.main,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontWeight: 500, letterSpacing: 0.3,
                              textTransform: 'uppercase' }}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700}
                        sx={{ color: color || 'text.primary', mt: 0.25 }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary">
                {sub}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};


// ---------------------------------------------------------------------------
// Verified-provider status banner + application dialog.
//
// Selling on the marketplace is reserved for VERIFIED ORGANIZATIONS.
// This banner is the single place a seller learns where they stand
// and (for org owners/admins) applies for verification.
// ---------------------------------------------------------------------------

const ProviderApplyDialog = ({
  open, onClose, onSubmit, mutations, orgName,
}) => {
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [attestation, setAttestation] = useState(false);

  const busy = mutations?.isLoading && mutations?.action === 'providerApply';
  const valid = description.trim().length > 0 && attestation;

  const submit = () => {
    onSubmit({
      website:       website.trim() || undefined,
      description:   description.trim(),
      contact_email: contactEmail.trim() || undefined,
      attestation:   true,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply to become a verified provider</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Verified providers can publish data signals to the marketplace.
          Our team reviews every application
          {orgName ? <> for <strong>{orgName}</strong></> : null} — you'll
          be notified once it's approved.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            label="Company website"
            placeholder="https://…"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
          />
          <TextField
            label="What data do you plan to sell?"
            placeholder="Describe the data signals you intend to publish and where the data comes from."
            multiline minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Contact email (optional)"
            placeholder="Defaults to your account email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={attestation}
                onChange={(e) => setAttestation(e.target.checked)}
              />
            )}
            label={(
              <Typography variant="body2">
                I confirm this organization owns — or is licensed to
                distribute — the data it intends to sell.
              </Typography>
            )}
          />
          {mutations?.error && mutations?.action === 'providerApply' && (
            <Alert severity="error">{String(mutations.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CMButtonOutlined onClick={onClose}>Cancel</CMButtonOutlined>
        <CMButton disabled={!valid || busy} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit application'}
        </CMButton>
      </DialogActions>
    </Dialog>
  );
};


const ProviderStatusBanner = ({
  sellerStatus, mutations, onApply, onClearMutations,
  showVerified = true,
}) => {
  const [applyOpen, setApplyOpen] = useState(false);

  // Auto-close the dialog once the application lands.
  useEffect(() => {
    if (applyOpen && mutations?.action === 'providerApply'
        && mutations?.result && !mutations?.isLoading && !mutations?.error) {
      setApplyOpen(false);
      onClearMutations && onClearMutations();
    }
  }, [applyOpen, mutations, onClearMutations]);

  if (!sellerStatus) return null;

  const status = sellerStatus.verification_status;
  const orgName = sellerStatus.org_name;

  if (status === 'verified') {
    // The confirmation banner is Overview-only; a role warning still
    // shows everywhere since it blocks publishing.
    if (!showVerified && sellerStatus.role_ok) return null;
    return (
      <Alert
        icon={<VerifiedIcon fontSize="inherit" />}
        severity="success"
        sx={{ mb: 2 }}
      >
        <strong>{orgName || 'Your organization'}</strong> is a verified
        provider.
        {!sellerStatus.role_ok && (
          <> Publishing requires the <strong>publisher</strong> or{' '}
          <strong>admin</strong> role in the organization — ask an org
          admin to update your role.</>
        )}
      </Alert>
    );
  }

  if (status === 'pending') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>{orgName || 'Your organization'}</strong>'s verified-provider
        application is under review. We'll notify you once it's approved.
      </Alert>
    );
  }

  const dialog = (
    <ProviderApplyDialog
      open={applyOpen}
      onClose={() => { setApplyOpen(false); onClearMutations && onClearMutations(); }}
      onSubmit={onApply}
      mutations={mutations}
      orgName={orgName}
    />
  );

  if (status === 'rejected') {
    return (
      <>
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={sellerStatus.can_apply ? (
            <CMButtonOutlined size="small" onClick={() => setApplyOpen(true)}>
              Re-apply
            </CMButtonOutlined>
          ) : null}
        >
          <strong>{orgName || 'Your organization'}</strong>'s application was
          declined
          {sellerStatus.application?.rejection_reason
            ? <>: {sellerStatus.application.rejection_reason}</>
            : '.'}
          {' '}You can update the details and re-apply.
        </Alert>
        {dialog}
      </>
    );
  }

  if (status === 'not_org') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Only <strong>verified organizations</strong> can sell on the
        marketplace. Create an organization (or switch to an
        organization-owned workspace) to apply for provider verification.
      </Alert>
    );
  }

  // status === 'none': org exists but has never applied.
  return (
    <>
      <Alert
        icon={<VerifiedIcon fontSize="inherit" />}
        severity="info"
        sx={{ mb: 2 }}
        action={sellerStatus.can_apply ? (
          <CMButton size="small" onClick={() => setApplyOpen(true)}>
            Apply for verification
          </CMButton>
        ) : null}
      >
        {sellerStatus.can_apply ? (
          <>Selling on the marketplace requires{' '}
          <strong>{orgName || 'your organization'}</strong> to be a verified
          provider. Apply below — our team reviews every application.</>
        ) : (
          <>Selling on the marketplace requires{' '}
          <strong>{orgName || 'your organization'}</strong> to be a verified
          provider. Ask an organization owner or admin to apply from this
          page.</>
        )}
      </Alert>
      {dialog}
    </>
  );
};


// ---------------------------------------------------------------------------
// Tab 1 — Dashboard
// ---------------------------------------------------------------------------

const DashboardTab = ({ myListings, earnings }) => {
  const theme = useTheme();
  const usdRate = useUsdRate();
  // Local helper so the JSX below stays readable. ALL credit amounts
  // shown to sellers are converted via this helper - the only place
  // raw credits appear in the UI now is the daily ClickHouse charge
  // ledger (which still says "credits" because that's what the BE
  // actually moves).
  const usd = (cr) => formatUSD(usdFromCredits(cr, usdRate));
  const listings = myListings?.listings || [];
  const totalSubs = listings.reduce(
    (acc, l) => acc + (l.subscribers_count || 0), 0,
  );
  const totalMRR = listings.reduce(
    (acc, l) => acc + (l.mrr_credits || 0), 0,
  );
  const balance = earnings?.balance || {};
  const accrued = Number(balance.balance_credits || 0);
  const lifetimeEarned = Number(balance.lifetime_credits || 0);
  const lifetimePaidOut = Number(balance.lifetime_paid_out || 0);
  const thisMonth = currentMonthRow(earnings?.monthly);

  return (
    // useFlexGap: without it Stack resets child margins, which breaks the
    // negative-margin alignment of direct <Grid container> children.
    <Stack spacing={2} useFlexGap>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KPITile
            label="Published"
            value={listings.length}
            sub={`${listings.filter((l) => l.status === 'published').length} active · ${listings.filter((l) => l.featured).length} featured`}
            icon={<AttachMoneyIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPITile
            label="Subscribers"
            value={totalSubs.toLocaleString()}
            sub="Active across all your listings"
            icon={<GroupIcon />}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPITile
            label="MRR (your share)"
            value={usd(totalMRR)}
            sub="Estimated monthly revenue"
            icon={<TrendingUpIcon />}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPITile
            label="Balance"
            value={usd(accrued)}
            sub={`Paid out so far: ${usd(lifetimePaidOut)}`}
            icon={<PaymentsIcon />}
            color={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      {/* This period + all-time earnings, side by side. */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                This period
              </Typography>
              <Typography variant="h3" fontWeight={800}
                          sx={{ color: 'primary.main' }}>
                {usd(thisMonth?.seller_credits || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {thisMonth?.month
                  ? `${thisMonth.charges_count || 0} charge${(thisMonth.charges_count || 0) === 1 ? '' : 's'} settled in ${thisMonth.month}`
                  : 'No settled charges this month'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                All-time earnings
              </Typography>
              <Typography variant="h3" fontWeight={800}>
                {usd(lifetimeEarned)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {usd(lifetimePaidOut)} paid out · {usd(accrued)} balance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top published signals — full width. */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Top published signals
          </Typography>
          {listings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Publish a signal from the Data Signals page to start earning.
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {[...listings]
                .sort((a, b) => (b.subscribers_count || 0) - (a.subscribers_count || 0))
                .slice(0, 5)
                .map((l) => (
                  <Stack key={l.listing_id} direction="row"
                         alignItems="center" spacing={1}
                         sx={{ py: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} sx={{ fontSize: 13 }}
                                  noWrap>
                        {l.title || l.signal_key}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.signal_key}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${l.subscribers_count || 0} subs`}
                      variant="outlined"
                    />
                    <Typography variant="caption"
                                sx={{ minWidth: 90, textAlign: 'right',
                                      fontWeight: 600 }}>
                      {usd(l.mrr_credits || 0)} / mo
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};


// ---------------------------------------------------------------------------
// Tab 2 — Published Signals (uses shared DataTable like Pipelines).
// ---------------------------------------------------------------------------

const PublishedSignalsTab = ({
  myListings, isLoading, onEdit, onDelist, onView,
}) => {
  const usdRate = useUsdRate();
  const usd = (cr) => formatUSD(usdFromCredits(cr, usdRate));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const rows = myListings?.listings || [];
  const total = rows.length;
  const paged = useMemo(
    () => rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [rows, page, rowsPerPage],
  );

  const STATUS_TONE = {
    published: 'success',
    draft:     'warning',
    delisted:  'neutral',
  };
  const STATUS_LABEL = {
    published: 'Live',
    draft:     'Draft',
    delisted:  'Delisted',
  };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (l) => (
        <EntityCell
          id={l.listing_id}
          tone={STATUS_TONE[l.status] || 'neutral'}
          label={STATUS_LABEL[l.status] || l.status || 'Unknown'}
        />
      ),
    },
    {
      key: 'name',
      label: 'Listing',
      width: '32%',
      sortable: false,
      render: (l) => (
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="body2" fontWeight={600}
                        sx={{ fontSize: 13, lineHeight: 1.3,
                              maxWidth: 320,
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap' }}>
              {l.title || l.signal_key}
            </Typography>
            {l.featured && (
              <Tooltip title="Featured">
                <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center"
                 sx={{ mt: 0.4 }}>
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontSize: 11 }}>
              {l.signal_key}
            </Typography>
            {l.kind && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ fontSize: 11 }}>
                · {KIND_LABELS[l.kind] || l.kind}
              </Typography>
            )}
          </Stack>
        </Box>
      ),
    },
    {
      key: 'subscribers',
      label: 'Subscribers',
      width: 110,
      align: 'right',
      render: (l) => (
        <Typography variant="body2" sx={{
          fontVariantNumeric: 'tabular-nums', fontWeight: 700,
        }}>
          {(l.subscribers_count || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'price',
      label: 'Pricing',
      width: 200,
      render: (l) => {
        const model = pricingModelOf(l);
        return (
          <Box>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', textTransform: 'uppercase',
                              letterSpacing: 0.3, fontWeight: 600 }}>
              {PRICING_MODEL_LABELS[model]}
            </Typography>
            <Typography variant="body2" sx={{
              fontVariantNumeric: 'tabular-nums', fontWeight: 600,
            }}>
              {priceSummary(l, usdRate)}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: 'mrr',
      label: 'MRR (your share)',
      width: 140,
      align: 'right',
      render: (l) => (
        <Typography variant="body2" sx={{
          fontVariantNumeric: 'tabular-nums', fontWeight: 600,
        }}>
          {usd(l.mrr_credits || 0)}
        </Typography>
      ),
    },
    {
      key: 'lifetime',
      label: 'Lifetime',
      width: 140,
      align: 'right',
      render: (l) => (
        <Typography variant="body2" sx={{
          fontVariantNumeric: 'tabular-nums', fontWeight: 600,
        }}>
          {usd(l.lifetime_seller_credits || 0)}
        </Typography>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      width: 110,
      render: (l) => (
        <Typography variant="caption" color="text.secondary">
          {niceDate(l.created_at)}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 100,
      align: 'right',
      stopRowClick: true,
      render: (l) => (
        <Stack direction="row" spacing={0} justifyContent="flex-end">
          <Tooltip title="View on marketplace">
            <span>
              <RowActions onOpen={() => onView(l)} openLabel="View on marketplace" />
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <DataTable
        columns={columns}
        rows={paged}
        getRowKey={(l) => l.listing_id}
        loading={isLoading}
        onRowClick={(l) => onEdit(l)}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        emptyState={{
          icon: <AttachMoneyIcon sx={{ fontSize: 32 }} />,
          title: 'No published signals yet',
          message: 'Open the Data Signals page, find a signal you want to monetize, and click the storefront icon on its card.',
        }}
      />
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Tab 3 — Payouts (payments received + earnings by month + charges)
// ---------------------------------------------------------------------------

const PayoutsTab = ({ earnings, isLoading }) => {
  const usdRate = useUsdRate();
  const usd = (cr) => formatUSD(usdFromCredits(cr, usdRate));
  if (isLoading && !earnings) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  }
  const monthly = earnings?.monthly || [];
  const payouts = earnings?.payouts || [];
  const charges = earnings?.recent_charges || [];

  return (
    <Stack spacing={2} useFlexGap>
      {/* Payments the platform team has made to this seller. */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Payments received
          </Typography>
          {payouts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No payments yet — payouts made to you will be listed here
              with date and amount.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.map((p, i) => (
                  <TableRow key={`${p.paid_at}-${i}`}>
                    <TableCell>{niceDate(p.paid_at)}</TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                      color: 'success.main',
                    }}>
                      {usd(p.amount_credits)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {p.reference || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Earnings by month
          </Typography>
          {monthly.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No settled earnings yet — monthly totals will appear here once
              buyers are charged.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Your share</TableCell>
                  <TableCell align="right">Gross charges</TableCell>
                  <TableCell align="right">Charges</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthly.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell sx={{ fontWeight: 600 }}>{m.month}</TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                    }}>
                      {usd(m.seller_credits)}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {usd(m.gross_credits)}
                    </TableCell>
                    <TableCell align="right">
                      {m.charges_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Recent charges
          </Typography>
          {charges.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No charges yet — they will appear here once a buyer subscribes.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Listing</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Your share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {charges.map((c) => (
                  <TableRow key={c.charge_id}>
                    <TableCell>
                      {c.kind === 'onetime'
                        ? niceDate(c.created_at)
                        : c.charge_date}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {c.listing_id}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.kind === 'onetime' ? 'One-time' : 'Daily'}
                        color={c.kind === 'onetime' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={c.status}
                            color={c.status === 'settled' ? 'success' : 'default'}
                            variant="outlined" />
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                    }}>
                      {usd(c.seller_payout_credits)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};


// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

// ``embedded`` renders inside the Settings shell: the shell owns the
// breadcrumbs, header and left-rail navigation, so this component
// skips its own breadcrumb/Tabs and takes the active view from the
// ``section`` prop ('dashboard' | 'published' | 'payouts')
// instead of the ``?tab=`` query param.
const MonetizationPage = ({ embedded = false, section }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = normalizeTabKey(
    embedded ? (section || 'dashboard') : searchParams.get('tab'),
  );
  const initialIdx = Math.max(0, TAB_KEYS.indexOf(tabParam || 'dashboard'));
  const [tabIdx, setTabIdx] = useState(initialIdx);

  // Keep state in sync with the URL when the user uses the back button
  // (or, embedded, with the shell's rail selection).
  useEffect(() => {
    const idx = Math.max(0, TAB_KEYS.indexOf(tabParam || 'dashboard'));
    setTabIdx(idx);
  }, [tabParam]);

  const myListings    = useSelector((s) => s.marketplace_my_listings.response);
  const myListingsLoading = useSelector((s) => s.marketplace_my_listings.isLoading);
  const earnings      = useSelector((s) => s.marketplace_earnings.response);
  const earningsLoading = useSelector((s) => s.marketplace_earnings.isLoading);
  const sellerStatus  = useSelector((s) => s.marketplace_seller_status.response);
  const mutations     = useSelector((s) => s.marketplace_mutations);

  const [delistTarget, setDelistTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchMyListings());
    dispatch(fetchEarnings());
    dispatch(fetchSellerStatus());
  }, [dispatch]);

  // Refresh after a delist completes.
  useEffect(() => {
    if (mutations?.action === 'delete' && mutations?.result
        && !mutations?.isLoading) {
      setDelistTarget(null);
      dispatch(fetchMyListings());
    }
  }, [mutations, dispatch]);

  const handleTabChange = (_, idx) => {
    setTabIdx(idx);
    setSearchParams({ tab: TAB_KEYS[idx] });
  };

  const headerActions = (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Refresh">
        <span>
          <CMButtonOutlined
            startIcon={<RefreshIcon />}
            onClick={() => {
              dispatch(fetchMyListings());
              dispatch(fetchEarnings());
              dispatch(fetchSellerStatus());
            }}
          >
            Refresh
          </CMButtonOutlined>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ width: '100%', mb: embedded ? 0 : 4 }}>
      {!embedded && (
        <PageBreadcrumbs
          items={[{ text: 'Monetization', icon: <AttachMoneyIcon /> }]}
          mb={0.5}
        />
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: embedded ? 2 : 2.5 }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: embedded ? 0 : 1 }}>
            {embedded
              ? `${TAB_LABEL[TAB_KEYS[tabIdx]]} — track published listings and earnings.`
              : 'Earn from data signals you publish to the marketplace — track published listings and earnings.'}
          </Typography>
        </Box>
        {headerActions}
      </Stack>

      {!embedded && (
        <Tabs
          value={tabIdx}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {TAB_KEYS.map((k) => (
            <Tab key={k} label={TAB_LABEL[k]} />
          ))}
        </Tabs>
      )}

      <ProviderStatusBanner
        sellerStatus={sellerStatus}
        mutations={mutations}
        showVerified={tabIdx === 0}
        onApply={(body) => dispatch(applyForProvider(body))}
        onClearMutations={() => dispatch(clearMutations())}
      />

      {tabIdx === 0 && (
        <DashboardTab
          myListings={myListings}
          earnings={earnings}
        />
      )}
      {tabIdx === 1 && (
        <PublishedSignalsTab
          myListings={myListings}
          isLoading={myListingsLoading}
          onEdit={(l) => navigate(`/marketplace/listings/${l.listing_id}/edit`)}
          onDelist={(l) => setDelistTarget(l)}
          onView={(l) => navigate(`/marketplace/listings/${l.listing_id}`)}
        />
      )}
      {tabIdx === 2 && (
        <PayoutsTab earnings={earnings} isLoading={earningsLoading} />
      )}

      <Dialog
        open={!!delistTarget}
        onClose={() => setDelistTarget(null)}
      >
        <DialogTitle>Delist listing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>
              {delistTarget?.title || delistTarget?.signal_key}
            </strong>{' '}
            will be removed from the marketplace and can no longer be
            subscribed to. Existing data is unaffected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CMButtonOutlined onClick={() => setDelistTarget(null)}>
            Cancel
          </CMButtonOutlined>
          <CMButtonError
            onClick={() => dispatch(deleteListing(delistTarget.listing_id))}
            disabled={mutations?.isLoading && mutations?.action === 'delete'}
          >
            Delist
          </CMButtonError>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonetizationPage;
